import { Link } from 'react-router-dom'

/**
 * A move within the calendar — a real link on the studio's own screens, a
 * button when the calendar is embedded in a portal (D-312).
 *
 * **Why this exists rather than a ternary at each site.** The teacher portal
 * embeds the studio calendar (D-300), and every `/calendar/*` route is gated on
 * `ViewAllSchedules` — the capability the embed exists to work around. So each
 * of these links sends a teacher out of her own portal to *"Not your page"*.
 *
 * The reported bug was the view switcher, and fixing only that would have left
 * **six more doors** onto the same dead end: back-to-the-week, the date picker,
 * previous/today/next day, and tapping a day in the week or month grid. They are
 * one bug with seven exits, so they get one component.
 *
 * **A link when it routes, a button when it does not.** Keeping the anchor on
 * the studio calendar is worth the branch: middle-click and "open in new tab"
 * are genuinely used on a timetable, and a button silently loses both.
 */
export default function CalendarLink({
  to,
  onActivate,
  className,
  title,
  children,
}: {
  /** Where it goes when it routes. Ignored when `onActivate` is supplied. */
  to: string
  /** Supplied when embedded: handle it in place instead of navigating. */
  onActivate?: () => void
  className?: string
  title?: string
  children: React.ReactNode
}) {
  if (onActivate) {
    return (
      <button type="button" onClick={onActivate} className={className} title={title}>
        {children}
      </button>
    )
  }

  return (
    <Link to={to} className={className} title={title}>
      {children}
    </Link>
  )
}
